"use client";

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Badge } from "@/app/components/ui/badge";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Slider } from "@/app/components/ui/slider";
import { Checkbox } from "@/app/components/ui/checkbox";
import { 
  Image, 
  Upload, 
  Download, 
  RotateCcw, 
  Settings, 
  Info, 
  AlertTriangle, 
  CheckCircle,
  Maximize,
  Minimize,
  BarChart3,
  History,
  Save,
  Trash2,
  RefreshCw,
  Database,
  Lock,
  Clock,
  Fingerprint,
  Layers,
  Target,
  Sparkles,
  Users,
  UserCheck,
  FileText,
  Shield,
  Eye,
  EyeOff,
  Crop,
  Monitor,
  Smartphone,
  Tablet,
  Camera,
  FileImage,
  Zap,
  Wand2,
  Palette,
  Move,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  Square,
  Maximize2,
  Minimize2,
  Circle,
  Scissors,
  Focus,
  ZoomIn,
  ZoomOut,
  Grid3X3,
  Grid2X2
} from "lucide-react";
import { toast } from "sonner";
import { PhotoModalCustom } from "@/app/components/ui/photo-modal-custom";

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  originalSize: { width: number; height: number };
  cropArea: CropArea;
  timestamp: Date;
}

interface CropHistory {
  id: string;
  image: ImageFile;
  timestamp: Date;
  settings: CropSettings;
}

interface CropSettings {
  aspectRatio: string;
  quality: number;
  format: string;
  showGrid: boolean;
  gridType: string;
}

type AspectRatio = "free" | "1:1" | "4:3" | "3:4" | "16:9" | "9:16" | "3:2" | "2:3" | "5:4" | "4:5";
type ImageFormat = "jpeg" | "png" | "webp" | "original";
type GridType = "none" | "rule-of-thirds" | "golden-ratio" | "center" | "diagonal";

const ASPECT_RATIOS = [
  { name: "Free", value: "free", icon: Crop },
  { name: "Square (1:1)", value: "1:1", icon: Square },
  { name: "Landscape (4:3)", value: "4:3", icon: Maximize2 },
  { name: "Portrait (3:4)", value: "3:4", icon: Minimize2 },
  { name: "Widescreen (16:9)", value: "16:9", icon: Monitor },
  { name: "Mobile (9:16)", value: "9:16", icon: Smartphone },
  { name: "Photo (3:2)", value: "3:2", icon: Camera },
  { name: "Portrait Photo (2:3)", value: "2:3", icon: Tablet },
  { name: "Classic (5:4)", value: "5:4", icon: Square },
  { name: "Portrait Classic (4:5)", value: "4:5", icon: Minimize2 }
];

const GRID_TYPES = [
  { name: "None", value: "none", icon: Grid3X3 },
  { name: "Rule of Thirds", value: "rule-of-thirds", icon: Grid3X3 },
  { name: "Golden Ratio", value: "golden-ratio", icon: Grid2X2 },
  { name: "Center Lines", value: "center", icon: Grid2X2 },
  { name: "Diagonal", value: "diagonal", icon: Grid2X2 }
];

// Image Crop Utility Class
class ImageCropper {
  static async cropImage(
    file: File,
    cropArea: CropArea,
    settings: CropSettings
  ): Promise<{ blob: Blob; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      img.onload = () => {
        const { x, y, width, height } = cropArea;
        
        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(
          img,
          x, y, width, height,
          0, 0, width, height
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve({ blob, width, height });
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          settings.format === 'original' ? file.type : `image/${settings.format}`,
          settings.quality / 100
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  static calculateAspectRatio(aspectRatio: string, containerWidth: number, containerHeight: number): { width: number; height: number } {
    if (aspectRatio === "free") {
      return { width: containerWidth, height: containerHeight };
    }

    const [ratioW, ratioH] = aspectRatio.split(':').map(Number);
    const ratio = ratioW / ratioH;

    let width = containerWidth;
    let height = containerWidth / ratio;

    if (height > containerHeight) {
      height = containerHeight;
      width = containerHeight * ratio;
    }

    return { width: Math.round(width), height: Math.round(height) };
  }

  static getFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export default function ImageCropPage() {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [history, setHistory] = useState<CropHistory[]>([]);
  const [activeTab, setActiveTab] = useState<"crop" | "settings">("crop");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [photoModalIndex, setPhotoModalIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Settings
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("free");
  const [quality, setQuality] = useState(90);
  const [format, setFormat] = useState<ImageFormat>("original");
  const [showGrid, setShowGrid] = useState(true);
  const [gridType, setGridType] = useState<GridType>("rule-of-thirds");

  const handleFileUpload = useCallback((files: FileList | null) => {
    if (!files) return;

    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      toast.error("Please select valid image files");
      return;
    }

    const newImages: ImageFile[] = imageFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      originalSize: { width: 0, height: 0 },
      cropArea: { x: 0, y: 0, width: 0, height: 0 },
      timestamp: new Date()
    }));

    // Load image dimensions and set initial crop area
    newImages.forEach(imageFile => {
      const img = new window.Image();
      img.onload = () => {
        imageFile.originalSize = { width: img.width, height: img.height };
        // Set initial crop area to center of image
        const centerX = img.width / 2;
        const centerY = img.height / 2;
        const cropSize = Math.min(img.width, img.height) * 0.8;
        imageFile.cropArea = {
          x: centerX - cropSize / 2,
          y: centerY - cropSize / 2,
          width: cropSize,
          height: cropSize
        };
        setImages(prev => [...prev, ...newImages]);
        if (!selectedImage) {
          setSelectedImage(imageFile);
        }
      };
      img.src = imageFile.preview;
    });

    toast.success(`Added ${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''}`);
  }, [selectedImage]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const cropImage = useCallback(async (image: ImageFile) => {
    if (!image) return;

    setIsProcessing(true);

    try {
      const settings: CropSettings = {
        aspectRatio,
        quality,
        format,
        showGrid,
        gridType
      };

      const result = await ImageCropper.cropImage(image.file, image.cropArea, settings);

      // Add to history
      const historyEntry: CropHistory = {
        id: Date.now().toString(),
        image: { ...image, cropArea: { ...image.cropArea } },
        timestamp: new Date(),
        settings
      };

      setHistory(prev => [historyEntry, ...prev.slice(0, 49)]);

      // Download the cropped image
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cropped_${image.file.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Image cropped and downloaded!");
    } catch (error) {
      toast.error("Failed to crop image");
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  }, [aspectRatio, quality, format, showGrid, gridType]);

  const clearImages = useCallback(() => {
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
    setSelectedImage(null);
    toast.success("Images cleared!");
  }, [images]);

  const removeImage = useCallback((id: string) => {
    const image = images.find(img => img.id === id);
    if (image) {
      URL.revokeObjectURL(image.preview);
    }
    setImages(prev => {
      const newImages = prev.filter(img => img.id !== id);
      if (selectedImage?.id === id) {
        setSelectedImage(newImages.length > 0 ? newImages[0] : null);
      }
      return newImages;
    });
    toast.success("Image removed!");
  }, [images, selectedImage]);

  const applyAspectRatio = useCallback((ratio: AspectRatio) => {
    setAspectRatio(ratio);
    if (selectedImage) {
      const { width, height } = ImageCropper.calculateAspectRatio(
        ratio,
        selectedImage.originalSize.width,
        selectedImage.originalSize.height
      );
      
      const centerX = selectedImage.originalSize.width / 2;
      const centerY = selectedImage.originalSize.height / 2;
      
      selectedImage.cropArea = {
        x: centerX - width / 2,
        y: centerY - height / 2,
        width,
        height
      };
      
      setSelectedImage({ ...selectedImage });
    }
    toast.success(`Applied ${ratio} aspect ratio`);
  }, [selectedImage]);

  const drawCropOverlay = useCallback(() => {
    if (!selectedImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new window.Image();
    img.onload = () => {
      // Set canvas size to match image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw image
      ctx.drawImage(img, 0, 0);

      // Draw crop area
      const { x, y, width, height } = selectedImage.cropArea;
      
      // Darken outside crop area
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Clear crop area
      ctx.clearRect(x, y, width, height);
      ctx.drawImage(img, x, y, width, height, x, y, width, height);

      // Draw crop border
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, width, height);

      // Draw grid if enabled
      if (showGrid && gridType !== 'none') {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 1;
        
        switch (gridType) {
          case 'rule-of-thirds':
            // Rule of thirds grid
            ctx.beginPath();
            ctx.moveTo(x + width / 3, y);
            ctx.lineTo(x + width / 3, y + height);
            ctx.moveTo(x + (width * 2) / 3, y);
            ctx.lineTo(x + (width * 2) / 3, y + height);
            ctx.moveTo(x, y + height / 3);
            ctx.lineTo(x + width, y + height / 3);
            ctx.moveTo(x, y + (height * 2) / 3);
            ctx.lineTo(x + width, y + (height * 2) / 3);
            ctx.stroke();
            break;
          case 'center':
            // Center lines
            ctx.beginPath();
            ctx.moveTo(x + width / 2, y);
            ctx.lineTo(x + width / 2, y + height);
            ctx.moveTo(x, y + height / 2);
            ctx.lineTo(x + width, y + height / 2);
            ctx.stroke();
            break;
          case 'diagonal':
            // Diagonal lines
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + width, y + height);
            ctx.moveTo(x + width, y);
            ctx.lineTo(x, y + height);
            ctx.stroke();
            break;
        }
      }

      // Draw corner handles
      const handleSize = 8;
      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(x - handleSize/2, y - handleSize/2, handleSize, handleSize);
      ctx.fillRect(x + width - handleSize/2, y - handleSize/2, handleSize, handleSize);
      ctx.fillRect(x - handleSize/2, y + height - handleSize/2, handleSize, handleSize);
      ctx.fillRect(x + width - handleSize/2, y + height - handleSize/2, handleSize, handleSize);
    };
    img.src = selectedImage.preview;
  }, [selectedImage, showGrid, gridType]);

  useEffect(() => {
    drawCropOverlay();
  }, [drawCropOverlay]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!selectedImage) return;
    
    setIsDragging(true);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = (e.clientX - rect.left) * (selectedImage.originalSize.width / rect.width);
    const y = (e.clientY - rect.top) * (selectedImage.originalSize.height / rect.height);
    
    setDragStart({ x, y });
  }, [selectedImage]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !selectedImage) return;
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = (e.clientX - rect.left) * (selectedImage.originalSize.width / rect.width);
    const y = (e.clientY - rect.top) * (selectedImage.originalSize.height / rect.height);
    
    const deltaX = x - dragStart.x;
    const deltaY = y - dragStart.y;
    
    const newCropArea = {
      ...selectedImage.cropArea,
      x: Math.max(0, Math.min(selectedImage.originalSize.width - selectedImage.cropArea.width, selectedImage.cropArea.x + deltaX)),
      y: Math.max(0, Math.min(selectedImage.originalSize.height - selectedImage.cropArea.height, selectedImage.cropArea.y + deltaY))
    };
    
    selectedImage.cropArea = newCropArea;
    setSelectedImage({ ...selectedImage });
    setDragStart({ x, y });
  }, [isDragging, selectedImage, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const getTotalSize = useMemo(() => {
    return images.reduce((total, img) => total + img.file.size, 0);
  }, [images]);

  const getAverageDimensions = useMemo(() => {
    if (images.length === 0) return { width: 0, height: 0 };
    const totalWidth = images.reduce((sum, img) => sum + img.originalSize.width, 0);
    const totalHeight = images.reduce((sum, img) => sum + img.originalSize.height, 0);
    return {
      width: Math.round(totalWidth / images.length),
      height: Math.round(totalHeight / images.length)
    };
  }, [images]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Image Crop Tool</h1>
        <p className="text-muted-foreground">
          Crop your images with precision. Use aspect ratio presets, grid overlays, and interactive cropping.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upload Area */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Images
              </CardTitle>
              <CardDescription>
                Drag and drop images or click to browse
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <Image className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium mb-2">Drop images here or click to browse</p>
                <p className="text-sm text-muted-foreground">
                  Supports JPG, PNG, WebP, and other image formats
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                />
              </div>
            </CardContent>
          </Card>

          {/* Crop Canvas */}
          {selectedImage && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crop className="h-5 w-5" />
                  Crop Image
                </CardTitle>
                <CardDescription>
                  Drag to move the crop area. Use the handles to resize.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    className="w-full h-auto max-h-96 cursor-move"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <Button 
                    onClick={() => cropImage(selectedImage)} 
                    disabled={isProcessing}
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Crop className="h-4 w-4 mr-2" />
                        Crop & Download
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={clearImages}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Image List */}
          {images.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Images
                </CardTitle>
                <CardDescription>
                  {images.length} image{images.length > 1 ? 's' : ''} uploaded
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {images.map((image, idx) => (
                    <div
                      key={image.id}
                      className={`border rounded-lg p-2 cursor-pointer transition-colors ${
                        selectedImage?.id === image.id ? 'border-blue-500 bg-blue-50' : 'hover:border-muted-foreground/50'
                      }`}
                      onClick={() => {
                        setSelectedImage(image);
                        setPhotoModalIndex(idx);
                        setIsPhotoModalOpen(true);
                      }}
                    >
                      <div className="relative">
                        <img
                          src={image.preview}
                          alt={image.file.name}
                          className="w-full h-20 object-cover rounded"
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          className="absolute top-1 right-1 h-5 w-5 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(image.id);
                          }}
                        >
                          ×
                        </Button>
                      </div>
                      <p className="text-xs font-medium truncate mt-1">{image.file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {image.originalSize.width} × {image.originalSize.height}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Photo modal viewer (opens on thumbnail click) */}
          <PhotoModalCustom
            isOpen={isPhotoModalOpen}
            onClose={() => setIsPhotoModalOpen(false)}
            photos={images.map((img) => ({ src: img.preview, alt: img.file.name, title: img.file.name }))}
            initialIndex={photoModalIndex}
            showThumbnails={true}
            showDownload={true}
            showZoom={true}
            showNavigation={true}
            onIndexChange={(index) => {
              setPhotoModalIndex(index);
              const img = images[index];
              if (img) setSelectedImage(img);
            }}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Crop Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Crop Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Aspect Ratio</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ASPECT_RATIOS.map((ratio) => {
                    const Icon = ratio.icon;
                    return (
                      <Button
                        key={ratio.value}
                        variant={aspectRatio === ratio.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => applyAspectRatio(ratio.value as AspectRatio)}
                        className="h-auto p-2 flex flex-col"
                      >
                        <Icon className="h-4 w-4 mb-1" />
                        <span className="text-xs">{ratio.name}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Grid Overlay</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="showGrid"
                    checked={showGrid}
                    onCheckedChange={(checked) => setShowGrid(checked as boolean)}
                  />
                  <Label htmlFor="showGrid">Show Grid</Label>
                </div>
                {showGrid && (
                  <Select value={gridType} onValueChange={(value) => setGridType(value as GridType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GRID_TYPES.map((grid) => (
                        <SelectItem key={grid.value} value={grid.value}>
                          {grid.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>Quality: {quality}%</Label>
                <Slider
                  value={[quality]}
                  onValueChange={(value) => setQuality(value[0])}
                  max={100}
                  min={1}
                  step={1}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="format">Format</Label>
                <Select value={format} onValueChange={(value) => setFormat(value as ImageFormat)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="original">Original</SelectItem>
                    <SelectItem value="jpeg">JPEG</SelectItem>
                    <SelectItem value="png">PNG</SelectItem>
                    <SelectItem value="webp">WebP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          {images.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{images.length}</div>
                    <div className="text-sm text-muted-foreground">Images</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{ImageCropper.getFileSize(getTotalSize)}</div>
                    <div className="text-sm text-muted-foreground">Total Size</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getAverageDimensions.width}</div>
                    <div className="text-sm text-muted-foreground">Avg Width</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getAverageDimensions.height}</div>
                    <div className="text-sm text-muted-foreground">Avg Height</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Recent History
                </CardTitle>
                <CardDescription>
                  Last {history.length} crop operation{history.length > 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.slice(0, 10).map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {entry.settings.aspectRatio}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp || Date.now()).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.image.cropArea.width} × {entry.image.cropArea.height}px
                        <br />
                        Quality: {entry.settings.quality}% • {entry.settings.format}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Use aspect ratio presets for consistent sizing</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Rule of thirds grid helps with composition</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Drag to move crop area, handles to resize</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>WebP format offers better compression</span>
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>Note:</strong> All image processing is done locally in your browser. 
              Your images are never uploaded to our servers.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
}
